import * as styleSheetLib from "@client/lib/styleSheet";
import { COMMON_DEFINITIONS_ALL } from "@client/settings/lib";
import {
  getDefinitionInner,
  iterateStructure,
  type SettingsStructure,
  type SettingsStructureSetting
} from "@client/settings/structure";
import { type GenericMap, type MainSettings, type SettingDefinition } from "@client/types";
import { type StyleSheetData } from "@client/types/StyleSheet";
import i18n from "@common/lib/i18n";
import { isNullOrUndefined } from "@common/lib/misc";
import { isEqual } from "lodash";
import { type StyleSheetIds, type UpgradeOpts } from ".";
import { joinDefined } from "../../misc";
import { generateId } from "../../random";
import {
  getResponsiveValue,
  getScreenSettings,
  mergeInheritedValues,
  processPossiblyResponsiveValue,
  setValue,
  splitFullPath,
  unwrapPossiblyResponsiveValue
} from "../../settings";
import { STYLE_SHEET_IDS } from "../../styleSheet";

// key - cache key
// value - path to optional flag
const pathsCache: GenericMap<GenericMap<boolean>> = {};

const addLink = (
  styleSheet: StyleSheetData,
  linkColorSetting: MainSettings,
  addedLinks: GenericMap<MainSettings>,
  folderName: string | undefined
): string => {
  const existingId = Object.keys(addedLinks).find((x) => isEqual(addedLinks[x], linkColorSetting));
  if (existingId) return existingId;

  let settings = {};

  processPossiblyResponsiveValue(linkColorSetting, (x, s) => {
    if (!x) return;

    settings = setValue(settings, "color", x, s);
    settings = setValue(settings, "hover.color", x, s);
  });

  const parentId = folderName ? styleSheetLib.upsertFolder(styleSheet.structure, "links", folderName) : "links";

  const id = generateId();
  styleSheet.structure = styleSheetLib.upsertItemStructure(styleSheet.structure, parentId, id, i18n.link, "link");
  styleSheet.settings = styleSheetLib.addItemSettings(styleSheet.settings, id, settings);

  addedLinks[id] = linkColorSetting;

  return id;
};

const processTextSettings = (
  pathsCacheKey: string,
  styleSheet: StyleSheetData,
  structure: SettingsStructure,
  settings: MainSettings,
  commonStructure: SettingsStructure | undefined,
  addedLinks: GenericMap<MainSettings>,
  styleSheetIds: StyleSheetIds,
  opts?: UpgradeOpts
): MainSettings => {
  const cacheKey = `${pathsCacheKey}-text`;
  if (!(cacheKey in pathsCache)) {
    const paths: GenericMap<boolean> = {};

    iterateStructure(
      structure,
      (x) => {
        if (x.prop && ["text", "h1", "h2", "h3", "h4", "h5", "h6"].includes(x.prop)) {
          paths[joinDefined(".", x.path, x.prop)] = x.isOptional;
          return false;
        }
        return true;
      },
      commonStructure
    );

    pathsCache[cacheKey] = paths;
  }

  const paths = pathsCache[cacheKey];
  for (const path of Object.keys(paths)) {
    const splitPath = splitFullPath(path);

    const value = getResponsiveValue(settings, path);
    if (!value && paths[path]) continue; // can't create optional values

    const levelPath = joinDefined(".", splitPath.path, "level");
    const levelSetting = getResponsiveValue(settings, levelPath);
    const linkColorSetting = path.endsWith("text")
      ? getResponsiveValue(settings, joinDefined(".", path, "linkColor"))
      : undefined;

    const linkStyleSheetId = linkColorSetting
      ? addLink(styleSheet, linkColorSetting, addedLinks, opts?.folderName)
      : path.endsWith("text") && opts?.forceStyleSheets
      ? styleSheetIds.link
      : undefined;

    let wasLinkAdded = false;
    let wasColorSet = false;
    let wasSetStylesheet = false;
    processPossiblyResponsiveValue(value ?? {}, (x, s) => {
      const level = unwrapPossiblyResponsiveValue(levelSetting, s);

      const textTag = level ?? splitPath.name;

      const hasCustomText = x && (x.font || x.size || x.line || x.letter || x.weight);
      if (
        !opts?.forceStyleSheets &&
        !level &&
        !hasCustomText &&
        !(styleSheetIds.colors[textTag] in styleSheet.settings) &&
        !linkColorSetting
      )
        return x;

      const addColor = (v: any, tag: string) => {
        if (v?.color || wasColorSet) {
          return v;
        }

        wasColorSet = true;
        return styleSheetIds.colors[tag] in styleSheet.settings
          ? { ...v, color: styleSheetIds.colors[tag] }
          : { ...v, color: styleSheetIds.colors.text };
      };

      const addLink = (v: any) => {
        if (linkStyleSheetId && !wasLinkAdded) {
          wasLinkAdded = true;
          return {
            ...v,
            s_link: linkStyleSheetId
          };
        }
        return v;
      };

      if ((opts?.forceStyleSheets || hasCustomText) && !wasSetStylesheet) {
        const v = hasCustomText
          ? {
              ...x,
              ...mergeInheritedValues(getScreenSettings(styleSheet.settings[styleSheetIds.text[textTag]], s), x),
              level,
              s_text: STYLE_SHEET_IDS.custom
            }
          : {
              ...x,
              level,
              s_text: styleSheetIds.text[textTag]
            };

        setValue(settings, path, addLink(addColor(v, textTag)), s);
        wasSetStylesheet = true;
      } else if (level) {
        setValue(
          settings,
          path,
          addLink(
            addColor(
              {
                ...x,
                level,
                s_text: styleSheetIds.text[level]
              },
              textTag
            )
          ),
          s
        );
      } else {
        setValue(settings, path, addLink(addColor(x, textTag)), s);
      }
    });

    delete settings[levelPath];
  }

  return settings;
};

export const processColorSettings = (
  pathsCacheKey: string,
  styleSheet: StyleSheetData,
  structure: SettingsStructure,
  defininitions: GenericMap<SettingDefinition> | null,
  settings: MainSettings,
  commonStructure: SettingsStructure | undefined,
  addedLinks: GenericMap<MainSettings>,
  styleSheetIds: StyleSheetIds,
  opts?: UpgradeOpts
): MainSettings => {
  const cacheKey = `${pathsCacheKey}-color`;
  if (!(cacheKey in pathsCache)) {
    const paths: GenericMap<boolean> = {};

    iterateStructure(
      structure,
      (x) => {
        if (!x.isSetting) return true;

        if (x.path && x.path.match(/(h1|h2|h3|h4|h5|h6|text)$/)) return false;

        if (x.key.toLowerCase().endsWith("color")) {
          const setting: SettingsStructureSetting =
            typeof x.structure === "string"
              ? {
                  def: x.structure
                }
              : (x.structure as SettingsStructureSetting);
          const definition = getDefinitionInner(COMMON_DEFINITIONS_ALL, x.key, setting, defininitions);
          if (definition.type !== "color") return true;

          paths[joinDefined(".", x.path, x.key)] = x.isOptional;
          return false;
        }
        return true;
      },
      commonStructure
    );

    pathsCache[cacheKey] = paths;
  }

  const paths = pathsCache[cacheKey];

  for (const path of Object.keys(paths)) {
    const splitPath = splitFullPath(path);

    const value = getResponsiveValue(settings, path);
    if (isNullOrUndefined(value) && paths[path] && (!splitPath.path || !getResponsiveValue(settings, splitPath.path)))
      continue; // can't create optional values

    let wasColorSet = false;
    processPossiblyResponsiveValue(value, (x, s) => {
      // users sometimes delete the value to remove the color, which defaults to white
      if (x === "") {
        wasColorSet = true;
        return setValue(settings, path, "#ffffff", s);
      }

      if (!x) {
        if (wasColorSet) return;

        if (styleSheetIds.colors.primary in styleSheet.settings) {
          wasColorSet = true;
          return setValue(settings, path, styleSheetIds.colors.primary, s);
        }
      } else {
        if (x !== x.trim()) {
          setValue(settings, path, x.trim(), s);
        }

        wasColorSet = true;
      }
    });
  }

  return settings;
};

// 1. fills in partial custom text and h1-6 settings from the stylesheet
// 2. moves level settings inside custom text
// (1 and 2 set s_text (style sheet inherited) value)
export const processStyleSheetSettings = (
  pathsCacheKey: string,
  styleSheet: StyleSheetData,
  structure: SettingsStructure,
  defininitions: GenericMap<SettingDefinition> | null,
  settings: MainSettings,
  commonStructure: SettingsStructure | undefined,
  addedLinks: GenericMap<MainSettings>,
  styleSheetIds: StyleSheetIds,
  opts?: UpgradeOpts
): MainSettings => {
  settings = processTextSettings(
    pathsCacheKey,
    styleSheet,
    structure,
    settings,
    commonStructure,
    addedLinks,
    styleSheetIds,
    opts
  );
  settings = processColorSettings(
    pathsCacheKey,
    styleSheet,
    structure,
    defininitions,
    settings,
    commonStructure,
    addedLinks,
    styleSheetIds,
    opts
  );

  return settings;
};
